import { useCallback, useEffect, useRef, useState } from "react";
import { Archive, FileText, Loader2, Pencil, Plus, Trash2, Upload } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  archivePlanReviewChecklist,
  archivePlanReviewPlanType,
  ChecklistExtractionConflictError,
  confirmChecklistExtraction,
  createPlanReviewPlanType,
  discardChecklistExtraction,
  extractPlanReviewChecklist,
  getChecklistExtraction,
  listPlanReviewPlanTypes,
  listResumableChecklistExtractions,
  updatePlanReviewChecklist,
  updatePlanReviewPlanType,
  type ChecklistExtractionStatus,
  type ChecklistProposalCoverage,
  type ChecklistResearchSummary,
  type PlanReviewChecklist,
  type PlanReviewChecklistCategory,
  type PlanReviewPlanType,
} from "@/api/plan_review";
import { ChecklistDetailDialog } from "@/components/plan_review/ChecklistDetailDialog";
import {
  ChecklistProposalEditor,
  toChecklistItems,
  toProposalRows,
  type ProposalRow,
} from "@/components/plan_review/ChecklistProposalEditor";
import { formatElapsedTime } from "@/components/plan_review/planReviewDisplay";
import {
  CHECKLIST_EXTRACTION_IN_FLIGHT_STATUSES,
  checklistExtractionQuiet,
  checklistExtractionStalled,
  checklistNotesLeftOut,
  checklistSentencesLeftOut,
  describeChecklistCoverage,
  describeChecklistExtractionPhase,
  describeChecklistExtractionStage,
  describeChecklistResearch,
  nextChecklistListPollMs,
} from "@/components/plan_review/planReviewStages";

const REVIEW_KIND_OPTIONS: Array<{ value: PlanReviewChecklistCategory; label: string; hint: string }> = [
  {
    value: "residential_site_plan",
    label: "Site plan",
    hint: "Setbacks, building layout, parking, and other site-plan checks.",
  },
  {
    value: "lot_split",
    label: "Lot split (plat)",
    hint: "Parcel split reviews: boundaries, bearings, lot area, new parcel lines.",
  },
  {
    value: "lot_combination",
    label: "Lot combination (plat)",
    hint: "Parcel merge reviews: combined geometry, lot-line removal.",
  },
];

function reviewKindLabel(kind: PlanReviewChecklistCategory) {
  return REVIEW_KIND_OPTIONS.find((option) => option.value === kind)?.label ?? kind;
}

// "smoke_lot_split.pdf" suggests "Smoke Lot Split". Only a word's first
// letter is raised, so "ENG-2139" and "PUD" keep their own casing.
function suggestedChecklistName(fileName: string | null | undefined) {
  if (!fileName) return "";
  return fileName
    .replace(/\.[^.]+$/, "")
    .replace(/[_-]+/g, " ")
    .trim()
    .replace(/(^|\s)(\p{Ll})/gu, (_match, space: string, letter: string) => space + letter.toUpperCase());
}

// The page-level banner sits behind a modal's overlay, so an error from a
// dialog's own action is shown inside that dialog instead.
function DialogError({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <p role="alert" className="rounded-md border border-red-200 bg-red-50 p-2 text-sm text-red-800" data-testid="dialog-error">
      {message}
    </p>
  );
}

type PlanTypeDialogState =
  | { mode: "create" }
  | { mode: "edit"; planType: PlanReviewPlanType }
  | null;

export function ChecklistManager({ municipalityId }: { municipalityId: number | null }) {
  const [planTypes, setPlanTypes] = useState<PlanReviewPlanType[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Errors from the plan type, rename, and archive dialogs. Kept apart from
  // the page banner so a dialog's error never outlives the dialog.
  const [dialogError, setDialogError] = useState<string | null>(null);
  // Bumped whenever one of those dialogs opens and on a municipality switch.
  // A save only touches the dialog when its generation is still current, so
  // a request from a dialog the switch force-closed cannot write its result
  // into a newer one.
  const dialogActionSeq = useRef(0);
  const [busy, setBusy] = useState(false);

  const [planTypeDialog, setPlanTypeDialog] = useState<PlanTypeDialogState>(null);
  const [planTypeName, setPlanTypeName] = useState("");
  const [planTypeDescription, setPlanTypeDescription] = useState("");
  const [planTypeKind, setPlanTypeKind] = useState<PlanReviewChecklistCategory>("residential_site_plan");

  const [addChecklistFor, setAddChecklistFor] = useState<PlanReviewPlanType | null>(null);
  const [checklistFile, setChecklistFile] = useState<File | null>(null);
  const [checklistName, setChecklistName] = useState("");
  const [extraction, setExtraction] = useState<ChecklistExtractionStatus | null>(null);
  const [extracting, setExtracting] = useState(false);
  // A finished proposal being fetched back for review after the planner
  // left and returned (ENG-2140).
  const [loadingProposal, setLoadingProposal] = useState(false);
  // Runs in flight and proposals awaiting review, across plan types. The
  // server owns this list so a run survives the dialog closing, a refresh,
  // or a planner reopening from another machine.
  const [resumable, setResumable] = useState<ChecklistExtractionStatus[]>([]);
  // Shown when the list has never loaded.
  const [resumableNotice, setResumableNotice] = useState<string | null>(null);
  // Set when the poll hits its ceiling with runs still in flight. The page
  // and the open dialog both say so. A new dialog run clears it, and so
  // does any list refresh that finds a run in flight, which starts the
  // poll again (see refreshResumable).
  const [pollCeilingHit, setPollCeilingHit] = useState(false);
  // The proposal under review. Local until Confirm: the planner edits,
  // drops, adds, and reorders here, and nothing durable exists before that.
  const [proposalRows, setProposalRows] = useState<ProposalRow[] | null>(null);
  const [extractedCoverage, setExtractedCoverage] = useState<ChecklistProposalCoverage | null>(null);
  const [extractedResearch, setExtractedResearch] = useState<ChecklistResearchSummary | null>(null);
  const coverageSentence = describeChecklistCoverage(extractedCoverage);
  const notesLeftOut = checklistNotesLeftOut(extractedCoverage);
  const sentencesLeftOut = checklistSentencesLeftOut(extractedCoverage);
  const researchSentence = describeChecklistResearch(extractedResearch, proposalRows?.length ?? 0);
  // Elapsed time counts from the planner's click, not the job's start, so
  // the queue wait shows too.
  const [extractionElapsedSeconds, setExtractionElapsedSeconds] = useState(0);
  const extractionStartedAt = useRef<number | null>(null);

  // Read-only inspection of a saved checklist. Editing its items is a
  // separate ticket; this is how a planner sees what was extracted.
  const [viewChecklist, setViewChecklist] = useState<PlanReviewChecklist | null>(null);

  const [renameChecklist, setRenameChecklist] = useState<PlanReviewChecklist | null>(null);
  const [renameChecklistName, setRenameChecklistName] = useState("");

  const [confirmArchive, setConfirmArchive] = useState<
    | { kind: "plan_type"; planType: PlanReviewPlanType }
    | { kind: "checklist"; checklist: PlanReviewChecklist }
    | null
  >(null);

  // Guards against a stale refresh overwriting a newer one.
  const requestSeq = useRef(0);
  const resumableSeq = useRef(0);
  // The newest list fetch that has landed, so the dialog can tell "my run
  // is not in the list" from "the list predates my run".
  const resumableLoadedSeq = useRef(0);

  // Resolves with the list, or null when it did not load. A failure before
  // any list has loaded is said out loud, since the cards would otherwise
  // silently hide every run in progress; later failures are left to the
  // poll's backoff, and the cards keep showing the last good list.
  const refreshResumable = useCallback(async (): Promise<ChecklistExtractionStatus[] | null> => {
    if (!municipalityId) {
      setResumable([]);
      return [];
    }
    const seq = ++resumableSeq.current;
    try {
      const list = await listResumableChecklistExtractions({ municipalityId });
      if (seq === resumableSeq.current) {
        resumableLoadedSeq.current = seq;
        setResumable(list);
        setResumableNotice(null);
        // Re-arms a poll that hit its ceiling. Unrelated refreshes (after a
        // rename or archive) re-arm it too, which is fine.
        if (list.some((item) => CHECKLIST_EXTRACTION_IN_FLIGHT_STATUSES.includes(item.status))) {
          setPollCeilingHit(false);
        }
      }
      return list;
    } catch {
      if (seq === resumableSeq.current && resumableLoadedSeq.current === 0) {
        setResumableNotice("Checklist uploads in progress could not be loaded. Refresh the page to try again.");
      }
      return null;
    }
  }, [municipalityId]);

  const refresh = useCallback(async () => {
    if (!municipalityId) {
      setPlanTypes([]);
      setResumable([]);
      return;
    }
    const seq = ++requestSeq.current;
    setLoading(true);
    try {
      const [list] = await Promise.all([listPlanReviewPlanTypes({ municipalityId }), refreshResumable()]);
      if (seq === requestSeq.current) {
        setPlanTypes(list);
        setError(null);
      }
    } catch (e) {
      if (seq === requestSeq.current) {
        setError(e instanceof Error ? e.message : "Failed to load plan types");
      }
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [municipalityId, refreshResumable]);

  // A dialog save that settles after a municipality switch must reload the
  // municipality now on screen, not the one its handler closed over.
  const refreshRef = useRef(refresh);
  useEffect(() => {
    refreshRef.current = refresh;
  }, [refresh]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // One poll drives both the cards and the open dialog: while any run is in
  // flight the list is refreshed, and the dialog reads its run's stage from
  // the same payload (see the effect below), so the two never disagree and
  // a background tab is not polled at all. Failures back off. The poll runs
  // while anything is in flight, the dialog's run included until it has
  // settled, easing off as a long run goes on, and stops at a ceiling of
  // visible time so a run that never settles cannot keep a tab polling
  // forever (ENG-2206). A quiet run is still polled: a queued run can start
  // late, and the server drops a dead one from the list.
  const dialogInFlight = !!extraction && CHECKLIST_EXTRACTION_IN_FLIGHT_STATUSES.includes(extraction.status);
  const resumableInFlight =
    dialogInFlight || resumable.some((item) => CHECKLIST_EXTRACTION_IN_FLIGHT_STATUSES.includes(item.status));
  useEffect(() => {
    if (!resumableInFlight) setPollCeilingHit(false);
  }, [resumableInFlight]);
  useEffect(() => {
    if (!resumableInFlight || pollCeilingHit) return;
    let cancelled = false;
    let timer: number | null = null;
    let failures = 0;
    let visibleMs = 0;
    const schedule = () => {
      const delay = nextChecklistListPollMs(failures, visibleMs);
      if (delay === null) {
        setPollCeilingHit(true);
        return;
      }
      timer = window.setTimeout(async () => {
        if (cancelled) return;
        if (document.visibilityState !== "hidden") {
          visibleMs += delay;
          // A slow list request counts toward the ceiling too, unless the tab
          // went hidden while it ran.
          const requestedAt = Date.now();
          failures = (await refreshResumable()) ? 0 : failures + 1;
          if (document.visibilityState !== "hidden") visibleMs += Date.now() - requestedAt;
        }
        if (!cancelled) schedule();
      }, delay);
    };
    schedule();
    return () => {
      cancelled = true;
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [resumableInFlight, pollCeilingHit, refreshResumable]);
  const pollStoppedNotice = pollCeilingHit && resumableInFlight;
  const dialogPollStopped = pollCeilingHit && dialogInFlight;
  const extractionQuiet = dialogInFlight && !!extraction && checklistExtractionQuiet(extraction);

  // The manager stays mounted across a municipality switch, so a dialog left
  // open would go on showing the previous municipality's checklist under the
  // new one. The upload dialog closes too; its run keeps going and is listed
  // again under its own municipality. The run sequence is bumped here so a
  // proposal fetch already in flight for the old municipality is dropped.
  useEffect(() => {
    setViewChecklist(null);
    setRenameChecklist(null);
    setConfirmArchive(null);
    setPlanTypeDialog(null);
    setDialogError(null);
    dialogActionSeq.current += 1;
    setPollCeilingHit(false);
    // The switch abandons any dialog save still in flight, and that save no
    // longer owns busy (see its finally), so it is released here.
    setBusy(false);
    setAddChecklistFor(null);
    dialogRunSeq.current += 1;
    setExtraction(null);
    setExtracting(false);
    setLoadingProposal(false);
    setProposalRows(null);
    setExtractedCoverage(null);
    setExtractedResearch(null);
  }, [municipalityId]);

  // The timer stops with the poll, so the dialog never counts up beside a
  // stage line nobody is refreshing any more.
  useEffect(() => {
    if (!extracting) return;
    const tick = () => {
      const startedAt = extractionStartedAt.current;
      setExtractionElapsedSeconds(startedAt ? Math.floor((Date.now() - startedAt) / 1000) : 0);
    };
    tick();
    if (dialogPollStopped) return;
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [extracting, dialogPollStopped]);

  // One place turns a status payload into dialog state, whether it came
  // from the upload response, the poll, or a resumed proposal. A run that
  // ended without a proposal (failed, stalled, or complete with nothing to
  // show) drops `extraction` so the file picker comes back and the planner
  // can try another file, as the failure copy tells them to.
  const applyExtractionStatus = useCallback((latest: ChecklistExtractionStatus) => {
    if (latest.status === "complete" && latest.result) {
      setExtraction(latest);
      setProposalRows(toProposalRows(latest.result.items));
      setExtractedCoverage(latest.result.coverage ?? null);
      setExtractedResearch(latest.result.research ?? null);
      setChecklistName((current) =>
        current.trim()
          ? current
          : latest.requested_name || latest.result?.name || suggestedChecklistName(latest.original_filename)
      );
      setExtracting(false);
    } else if (latest.status === "complete") {
      setError("The proposal could not be loaded. Try uploading the file again.");
      setExtraction(null);
      setExtracting(false);
    } else if (latest.status === "failed") {
      setError(latest.error_message || "Extraction failed");
      setExtraction(null);
      setExtracting(false);
    } else if (checklistExtractionStalled(latest)) {
      setError("Extraction stopped responding. Try uploading the file again.");
      setExtraction(null);
      setExtracting(false);
    } else {
      setExtraction(latest);
    }
  }, []);

  // The dialog follows its run through the shared list. While the run is
  // listed in flight its stage comes from there; once it is listed complete
  // the proposal is fetched once; and if it has left the list (failed,
  // stalled, confirmed or discarded elsewhere) one fetch says why.
  const settlingExtractionId = useRef<number | null>(null);
  // Any list fetch started at or before this point may predate the dialog's
  // run; only a newer one can say the run has left the list.
  const dialogListSeq = useRef(0);
  useEffect(() => {
    if (!extraction?.id || !dialogInFlight) return;
    const listed = resumable.find((item) => item.id === extraction.id);
    if (listed && CHECKLIST_EXTRACTION_IN_FLIGHT_STATUSES.includes(listed.status)) {
      setExtraction(listed);
      return;
    }
    if (!listed && resumableLoadedSeq.current <= dialogListSeq.current) return;
    if (settlingExtractionId.current === extraction.id) return;
    settlingExtractionId.current = extraction.id;
    const id = extraction.id;
    const run = dialogRunSeq.current;
    void (async () => {
      try {
        const latest = await getChecklistExtraction(id);
        if (run !== dialogRunSeq.current) return;
        if (latest.confirmed_at || latest.discarded_at) {
          // Settled from another window. refresh clears the error, so the
          // message is set after it, as confirm does.
          setExtraction(null);
          setExtracting(false);
          await refresh();
          if (run !== dialogRunSeq.current) return;
          setError(
            latest.confirmed_at
              ? "This upload was already saved as a checklist."
              : "This upload was discarded from another window."
          );
        } else if (!listed && CHECKLIST_EXTRACTION_IN_FLIGHT_STATUSES.includes(latest.status) && !latest.stalled) {
          // Off the list but alive by the server's own account (its plan
          // type was archived, say): keep following it here. The next list
          // tick fetches it again.
          setExtraction(latest);
        } else {
          applyExtractionStatus(latest);
        }
      } catch {
        // Leave the run in flight; the next list tick brings it back here.
      } finally {
        if (settlingExtractionId.current === id) settlingExtractionId.current = null;
      }
    })();
  }, [resumable, extraction?.id, dialogInFlight, applyExtractionStatus, refresh]);

  const openCreatePlanType = () => {
    setPlanTypeName("");
    setPlanTypeDescription("");
    setPlanTypeKind("residential_site_plan");
    setDialogError(null);
    dialogActionSeq.current += 1;
    setPlanTypeDialog({ mode: "create" });
  };

  const openEditPlanType = (planType: PlanReviewPlanType) => {
    setPlanTypeName(planType.name);
    setPlanTypeDescription(planType.description ?? "");
    setPlanTypeKind(planType.review_kind);
    setDialogError(null);
    dialogActionSeq.current += 1;
    setPlanTypeDialog({ mode: "edit", planType });
  };

  // Escape, the overlay, and the close button are ignored while a save is in
  // flight, like the disabled Cancel: otherwise its rejection lands after the
  // dialog is gone (shown nowhere) or in the next dialog opened.
  const closePlanTypeDialog = () => {
    setPlanTypeDialog(null);
    setDialogError(null);
  };

  const openRenameChecklist = (checklist: PlanReviewChecklist) => {
    setDialogError(null);
    dialogActionSeq.current += 1;
    setRenameChecklist(checklist);
    setRenameChecklistName(checklist.name);
  };

  const closeRenameChecklist = () => {
    setRenameChecklist(null);
    setDialogError(null);
  };

  const openConfirmArchive = (target: NonNullable<typeof confirmArchive>) => {
    setDialogError(null);
    dialogActionSeq.current += 1;
    setConfirmArchive(target);
  };

  const closeConfirmArchive = () => {
    setConfirmArchive(null);
    setDialogError(null);
  };

  const handleSavePlanType = async () => {
    if (!municipalityId || !planTypeDialog) return;
    const name = planTypeName.trim();
    if (!name) {
      setDialogError("Plan type name is required");
      return;
    }
    setBusy(true);
    setDialogError(null);
    const action = dialogActionSeq.current;
    try {
      if (planTypeDialog.mode === "create") {
        await createPlanReviewPlanType({
          municipalityId,
          name,
          description: planTypeDescription.trim() || undefined,
          reviewKind: planTypeKind,
        });
      } else {
        await updatePlanReviewPlanType({
          planTypeId: planTypeDialog.planType.id,
          name,
          description: planTypeDescription.trim(),
          reviewKind: planTypeKind,
        });
      }
      // The generation guards the dialog only; the saved change still shows.
      if (action === dialogActionSeq.current) closePlanTypeDialog();
      await refreshRef.current();
    } catch (e) {
      if (action !== dialogActionSeq.current) return;
      setDialogError(e instanceof Error ? e.message : "Failed to save plan type");
    } finally {
      if (action === dialogActionSeq.current) setBusy(false);
    }
  };

  // Closing only drops the dialog's view of the run: the job keeps going
  // server side and the card lists it until it is confirmed or discarded.
  // The poll and any error still have to stop here, or they keep mutating
  // state behind (or after) the dialog.
  // Bumped whenever the dialog's run is reset (close, reopen, municipality
  // switch), so a fetch started for an earlier run cannot land on this one.
  const dialogRunSeq = useRef(0);

  const resetExtractionState = () => {
    dialogRunSeq.current += 1;
    setExtraction(null);
    setExtracting(false);
    setLoadingProposal(false);
    setProposalRows(null);
    setExtractedCoverage(null);
    setExtractedResearch(null);
    setExtractionElapsedSeconds(0);
    extractionStartedAt.current = null;
    settlingExtractionId.current = null;
    setError(null);
    // A stop belongs to the run it happened under, not the next one.
    setPollCeilingHit(false);
  };

  const closeAddChecklist = () => {
    setAddChecklistFor(null);
    resetExtractionState();
    void refreshResumable();
  };

  const openAddChecklist = (planType: PlanReviewPlanType) => {
    setChecklistFile(null);
    setChecklistName("");
    resetExtractionState();
    setAddChecklistFor(planType);
  };

  // Picks a run back up from the card. An in-flight run resumes polling;
  // a finished proposal is fetched with its rows for review. Elapsed time
  // counts from the upload, since that is what the planner has been
  // waiting on.
  const resumeExtraction = async (planType: PlanReviewPlanType, status: ChecklistExtractionStatus) => {
    openAddChecklist(planType);
    // The name they typed before starting rides on the extraction, so it
    // is not lost with the dialog.
    setChecklistName(status.requested_name ?? "");
    extractionStartedAt.current = Date.parse(status.created_at);
    if (CHECKLIST_EXTRACTION_IN_FLIGHT_STATUSES.includes(status.status)) {
      dialogListSeq.current = resumableSeq.current;
      setExtraction(status);
      setExtracting(true);
      return;
    }
    // `extraction` is only set once the rows are here, so a failed fetch
    // leaves a plain upload dialog rather than one with nothing to do.
    setLoadingProposal(true);
    const run = dialogRunSeq.current;
    try {
      const latest = await getChecklistExtraction(status.id);
      if (run !== dialogRunSeq.current) return;
      applyExtractionStatus(latest);
    } catch (e) {
      if (run !== dialogRunSeq.current) return;
      setError(e instanceof Error ? e.message : "Failed to load the proposal");
    } finally {
      if (run === dialogRunSeq.current) setLoadingProposal(false);
    }
  };

  // Drops a proposal the planner does not want. Offered once the run is
  // complete, or once an in-flight run has been quiet long enough that it
  // is probably never coming (a dead queue), or once the poll has stopped
  // following it: while a run is live, closing keeps it, and an accidental
  // discard would lose exactly the work this dialog protects.
  const canDiscard =
    !!extraction && (!!proposalRows || checklistExtractionQuiet(extraction) || dialogPollStopped);
  const handleDiscardExtraction = async () => {
    if (!extraction?.id) return;
    setBusy(true);
    setError(null);
    try {
      await discardChecklistExtraction(extraction.id);
      closeAddChecklist();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to discard the proposal");
    } finally {
      setBusy(false);
    }
  };

  const handleExtract = async () => {
    if (!municipalityId || !addChecklistFor || !checklistFile) return;
    setError(null);
    setPollCeilingHit(false);
    setExtracting(true);
    setProposalRows(null);
    extractionStartedAt.current = Date.now();
    setExtractionElapsedSeconds(0);
    const name = checklistName.trim() || suggestedChecklistName(checklistFile.name);
    setChecklistName(name);
    try {
      const status = await extractPlanReviewChecklist({
        municipalityId,
        file: checklistFile,
        planTypeId: addChecklistFor.id,
        category: addChecklistFor.review_kind,
        name,
      });
      dialogListSeq.current = resumableSeq.current;
      applyExtractionStatus(status);
      // The card behind the dialog shows the run from now on, and the
      // dialog follows it there.
      void refreshResumable();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to extract checklist");
      setExtracting(false);
    }
  };

  // Confirm is the approval step: the reviewed rows become the checklist,
  // under the plan type the upload was proposed for. The server copies the
  // source file and plan type from the extraction, so only the planner's
  // edits travel.
  const handleConfirmChecklist = async () => {
    if (!addChecklistFor || !extraction?.id || !proposalRows) return;
    const name = checklistName.trim();
    if (!name) {
      setError("Checklist name is required");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await confirmChecklistExtraction({
        extractionId: extraction.id,
        name,
        items: toChecklistItems(proposalRows),
      });
      closeAddChecklist();
      await refresh();
    } catch (e) {
      if (e instanceof ChecklistExtractionConflictError) {
        // A double submit, or a second tab already saved or discarded it:
        // there is nothing left to confirm, so the dialog closes and the
        // refreshed list shows what exists. closeAddChecklist and refresh
        // both clear the error, so the message is set last.
        closeAddChecklist();
        await refresh();
        setError(e.message);
      } else {
        setError(e instanceof Error ? e.message : "Failed to save checklist");
      }
    } finally {
      setBusy(false);
    }
  };

  const handleRenameChecklist = async () => {
    if (!renameChecklist) return;
    const name = renameChecklistName.trim();
    if (!name) return;
    setBusy(true);
    setDialogError(null);
    const action = dialogActionSeq.current;
    try {
      await updatePlanReviewChecklist({ checklistId: renameChecklist.id, name });
      // The generation guards the dialog only; the saved change still shows.
      if (action === dialogActionSeq.current) closeRenameChecklist();
      await refreshRef.current();
    } catch (e) {
      if (action !== dialogActionSeq.current) return;
      setDialogError(e instanceof Error ? e.message : "Failed to rename checklist");
    } finally {
      if (action === dialogActionSeq.current) setBusy(false);
    }
  };

  const handleConfirmArchive = async () => {
    if (!confirmArchive) return;
    setBusy(true);
    setDialogError(null);
    const action = dialogActionSeq.current;
    try {
      if (confirmArchive.kind === "plan_type") {
        await archivePlanReviewPlanType(confirmArchive.planType.id);
      } else {
        await archivePlanReviewChecklist(confirmArchive.checklist.id);
      }
      // The generation guards the dialog only; the saved change still shows.
      if (action === dialogActionSeq.current) closeConfirmArchive();
      await refreshRef.current();
    } catch (e) {
      if (action !== dialogActionSeq.current) return;
      setDialogError(e instanceof Error ? e.message : "Failed to archive");
    } finally {
      if (action === dialogActionSeq.current) setBusy(false);
    }
  };

  const canConfirm =
    !busy &&
    !!checklistName.trim() &&
    !!proposalRows &&
    proposalRows.length > 0 &&
    proposalRows.every((row) => row.label.trim().length > 0);

  // The server owns this: archived checklists lock the kind too, and they
  // are not in the catalog, so counting the nested list here would be wrong.
  const editingKindLocked = planTypeDialog?.mode === "edit" && planTypeDialog.planType.review_kind_locked;

  if (!municipalityId) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center p-6 text-sm text-muted-foreground">
        Select a municipality to manage its checklists.
      </div>
    );
  }

  return (
    <div className="ordinal-scrollbar flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4" data-testid="checklist-manager">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Checklists</h2>
          <p className="text-sm text-muted-foreground">
            Plan types your municipality reviews, and the checklists nested under each. Both logged and
            unlogged reviews pick from here.
          </p>
        </div>
        <Button onClick={openCreatePlanType} data-testid="add-plan-type">
          <Plus className="mr-2 h-4 w-4" />
          Add plan type
        </Button>
      </div>

      {error ? (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</div>
      ) : null}
      {resumableNotice || pollStoppedNotice ? (
        <div
          className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800"
          data-testid="resumable-notice"
        >
          {resumableNotice ??
            "Stopped checking on checklist uploads automatically. Refresh the page to keep following them."}
        </div>
      ) : null}

      {loading && planTypes.length === 0 ? (
        <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading plan types...
        </div>
      ) : null}

      {planTypes.map((planType) => (
        <Card key={planType.id} className="p-4" data-testid={`plan-type-${planType.id}`}>
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="truncate text-base font-semibold text-foreground">{planType.name}</h3>
                <Badge variant="outline">{reviewKindLabel(planType.review_kind)}</Badge>
              </div>
              {planType.description ? (
                <p className="mt-0.5 text-sm text-muted-foreground">{planType.description}</p>
              ) : null}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => openAddChecklist(planType)}>
                <Upload className="mr-2 h-4 w-4" />
                Add checklist
              </Button>
              <Button
                variant="ghost"
                size="icon"
                aria-label={`Edit plan type ${planType.name}`}
                onClick={() => openEditPlanType(planType)}
              >
                <Pencil className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                aria-label={`Archive plan type ${planType.name}`}
                onClick={() => openConfirmArchive({ kind: "plan_type", planType })}
              >
                <Archive className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <ul className="mt-3 divide-y rounded-md border">
            {(planType.checklists ?? []).map((checklist) => (
              <li key={checklist.id} className="flex items-center justify-between gap-2 px-3 py-2">
                {/* The row opens the items; the pencil still only renames. */}
                <button
                  type="button"
                  className="flex min-w-0 flex-1 items-center gap-2 rounded-sm text-left hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  aria-label={`View checklist ${checklist.name}`}
                  onClick={() => setViewChecklist(checklist)}
                >
                  <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="truncate text-sm text-foreground">{checklist.name}</span>
                  {checklist.system_default ? <Badge variant="secondary">Ordinal default</Badge> : null}
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {checklist.items.length} item{checklist.items.length === 1 ? "" : "s"}
                  </span>
                </button>
                {checklist.system_default ? null : (
                  <div className="flex items-center">
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Rename checklist ${checklist.name}`}
                      onClick={() => openRenameChecklist(checklist)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Archive checklist ${checklist.name}`}
                      onClick={() => openConfirmArchive({ kind: "checklist", checklist })}
                    >
                      <Archive className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </li>
            ))}
            {(planType.checklists ?? []).length === 0 ? (
              <li className="px-3 py-4 text-sm text-muted-foreground">
                No checklists yet. Upload one to extract its requirements.
              </li>
            ) : null}
          </ul>

          {/* Uploads still running or waiting for review. The planner can
              leave and come back; nothing here is lost by closing the
              dialog. */}
          {resumable.some((item) => item.plan_type_id === planType.id) ? (
            <ul className="mt-2 space-y-1" data-testid={`resumable-extractions-${planType.id}`}>
              {resumable
                .filter((item) => item.plan_type_id === planType.id)
                .map((item) => {
                  const inFlight = CHECKLIST_EXTRACTION_IN_FLIGHT_STATUSES.includes(item.status);
                  return (
                    <li
                      key={item.id}
                      className="flex items-center justify-between gap-2 rounded-md border border-dashed px-3 py-2"
                      data-testid={`resumable-extraction-${item.id}`}
                    >
                      <div className="flex min-w-0 flex-1 items-center gap-2 text-sm">
                        {inFlight ? (
                          <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
                        ) : (
                          <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                        )}
                        <span className="truncate text-foreground">{item.original_filename}</span>
                        <span className="truncate text-sm text-muted-foreground">
                          {inFlight
                            ? checklistExtractionQuiet(item)
                              ? "No progress in 10 minutes"
                              : item.status === "retrying"
                              ? "The parser had a hiccup; retrying shortly"
                              : `${describeChecklistExtractionStage(item.current_stage)}…`
                            : `Ready to review${typeof item.row_count === "number" ? `: ${item.row_count} row${item.row_count === 1 ? "" : "s"}` : ""}`}
                        </span>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => void resumeExtraction(planType, item)}>
                        {inFlight ? "Open" : "Review"}
                      </Button>
                    </li>
                  );
                })}
            </ul>
          ) : null}
        </Card>
      ))}

      {/* Add / edit plan type */}
      <Dialog open={planTypeDialog !== null} onOpenChange={(open) => !open && !busy && closePlanTypeDialog()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{planTypeDialog?.mode === "edit" ? "Edit plan type" : "Add plan type"}</DialogTitle>
            <DialogDescription>
              Plan types group checklists and pick the review style Ordinal runs.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <label className="text-sm font-medium text-foreground" htmlFor="plan-type-name">
                Name
              </label>
              <Input
                id="plan-type-name"
                value={planTypeName}
                onChange={(event) => setPlanTypeName(event.target.value)}
                placeholder="e.g. Plat of Survey"
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-foreground" htmlFor="plan-type-description">
                Description (optional)
              </label>
              <Input
                id="plan-type-description"
                value={planTypeDescription}
                onChange={(event) => setPlanTypeDescription(event.target.value)}
                placeholder="What submittals belong here"
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-foreground" htmlFor="plan-type-kind">
                Review style
              </label>
              <select
                id="plan-type-kind"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground disabled:opacity-70"
                value={planTypeKind}
                disabled={editingKindLocked}
                onChange={(event) => setPlanTypeKind(event.target.value as PlanReviewChecklistCategory)}
              >
                {REVIEW_KIND_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground">
                {editingKindLocked
                  ? "Review style is locked while checklists use this plan type."
                  : REVIEW_KIND_OPTIONS.find((option) => option.value === planTypeKind)?.hint}
              </p>
            </div>
            <DialogError message={dialogError} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closePlanTypeDialog} disabled={busy}>
              Cancel
            </Button>
            <Button onClick={handleSavePlanType} disabled={busy || !planTypeName.trim()} data-testid="save-plan-type">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {planTypeDialog?.mode === "edit" ? "Save changes" : "Add plan type"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add checklist via upload + extract */}
      <Dialog open={addChecklistFor !== null} onOpenChange={(open) => !open && closeAddChecklist()}>
        {/* A flex column whose body is the only scroller: with the panel
            itself scrolling, a long proposal pushed Confirm below the fold
            behind a second, nested scrollbar. */}
        <DialogContent className="flex max-h-[85vh] flex-col overflow-y-hidden sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add checklist to {addChecklistFor?.name}</DialogTitle>
            <DialogDescription>
              Upload a checklist document; Ordinal lists every requirement row it contains for you to review.
              You can close this while it runs and come back. Confirming keeps you here. No review is started.
            </DialogDescription>
          </DialogHeader>
          {/* min-w-0: DialogContent is a grid; without it a nowrap source
              line in the editor sets the track's min-content width and the
              dialog scrolls sideways instead of truncating. */}
          <div className="ordinal-scrollbar min-w-0 flex-1 space-y-3 overflow-y-auto">
            {extraction ? (
              // Once a run exists the file is fixed; a resumed run may not
              // even have the File object any more.
              <p className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="extraction-file">
                <FileText className="h-4 w-4 shrink-0" />
                <span className="truncate">{extraction.original_filename}</span>
              </p>
            ) : (
              <Input
                type="file"
                accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.txt,.md"
                onChange={(event) => {
                  const file = event.target.files?.[0] || null;
                  setChecklistFile(file);
                  setChecklistName((current) => (current.trim() ? current : suggestedChecklistName(file?.name)));
                }}
              />
            )}
            <div className="space-y-1">
              <label className="text-sm font-medium text-foreground" htmlFor="new-checklist-name">
                Checklist name
              </label>
              <Input
                id="new-checklist-name"
                value={checklistName}
                onChange={(event) => setChecklistName(event.target.value)}
                placeholder="Suggested from the file name"
                // The name is saved with the upload when the run starts and
                // is editable again on the proposal; while the run is going
                // an edit here would not survive closing the dialog.
                disabled={extracting}
              />
            </div>
            {extracting ? (
              <div
                className={
                  dialogPollStopped || extractionQuiet
                    ? "flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-sm text-amber-800"
                    : "flex items-center gap-2 text-sm text-muted-foreground"
                }
                data-testid="extraction-progress"
              >
                {dialogPollStopped || extractionQuiet ? null : <Loader2 className="h-4 w-4 shrink-0 animate-spin" />}
                {/* A screen reader hears the phase change, going quiet, or
                    the stop, not each row counted or each second ticked. The
                    page notice sits behind the overlay, so a stopped poll is
                    said here too. */}
                <span className="sr-only" role="status" data-testid="extraction-announcement">
                  {dialogPollStopped
                    ? "Stopped checking on this upload automatically."
                    : extractionQuiet
                      ? "This upload hasn't made progress in 10 minutes."
                      : extraction?.status === "retrying"
                        ? "The parser had a hiccup; retrying shortly"
                        : describeChecklistExtractionPhase(extraction?.current_stage)}
                </span>
                <span className="flex-1" data-testid="extraction-stage">
                  {dialogPollStopped ? (
                    <span data-testid="extraction-poll-stopped">
                      Stopped checking on this upload automatically. Refresh the page to keep following it.
                    </span>
                  ) : extractionQuiet ? (
                    // 10 minutes is PlanReviewChecklistExtraction::STALE_AFTER, which `quiet` is judged by.
                    <span data-testid="extraction-quiet">
                      This upload hasn't made progress in 10 minutes. It may still start, or you can discard it.
                    </span>
                  ) : (
                    <>
                      {extraction?.status === "retrying"
                        ? "The parser had a hiccup; retrying shortly"
                        : describeChecklistExtractionStage(extraction?.current_stage)}
                      …
                    </>
                  )}
                </span>
                <span className="tabular-nums">{formatElapsedTime(extractionElapsedSeconds)} elapsed</span>
              </div>
            ) : null}
            {extracting ? (
              <p className="text-xs text-muted-foreground" data-testid="extraction-leave-hint">
                You can close this and keep working. The run continues, and it shows under {addChecklistFor?.name} when
                it is ready to review.
              </p>
            ) : null}
            {loadingProposal ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="proposal-loading">
                <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
                Loading the proposal…
              </div>
            ) : null}
            {/* The page-level banner sits behind the modal overlay, so the
                dialog needs its own copy of the error. */}
            {addChecklistFor ? <DialogError message={error} /> : null}
            {proposalRows ? (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground" data-testid="proposed-row-count">
                  Found {proposalRows.length} requirement row{proposalRows.length === 1 ? "" : "s"}
                  {extraction?.original_filename ? ` in ${extraction.original_filename}` : ""}. Review them, then
                  confirm to add the checklist to {addChecklistFor?.name}.
                </p>
                {coverageSentence ? (
                  <p
                    className={
                      extractedCoverage?.structure === "none" ||
                      (extractedCoverage?.added_from_source ?? 0) > 0 ||
                      (extractedCoverage?.parser_shortfall ?? 0) > 0 ||
                      extractedCoverage?.vision_check === "failed"
                        ? "text-sm text-amber-700 dark:text-amber-400"
                        : "text-sm text-muted-foreground"
                    }
                    data-testid="proposed-row-coverage"
                  >
                    {coverageSentence}
                  </p>
                ) : null}
                {notesLeftOut.length > 0 ? (
                  // The notes a second check left out (ENG-2100), with its
                  // reason for each, so a wrong call can be caught and the
                  // line added by hand.
                  <details className="text-sm text-muted-foreground" data-testid="proposed-row-notes-left-out">
                    <summary className="cursor-pointer">
                      Show the {notesLeftOut.length === 1 ? "note" : `${notesLeftOut.length} notes`} left out
                    </summary>
                    <ul className="mt-1 space-y-2 border-l border-border pl-3">
                      {notesLeftOut.map((note, index) => (
                        <li key={`${index}-${note.text.slice(0, 24)}`}>
                          <p className="text-foreground">“{note.text}”</p>
                          {note.reason ? <p className="text-xs">Left out: {note.reason}</p> : null}
                        </li>
                      ))}
                    </ul>
                  </details>
                ) : null}
                {sentencesLeftOut.length > 0 ? (
                  // The sentences of running text the same check left out
                  // (ENG-2101), so a wrong call can be caught the same way.
                  <details className="text-sm text-muted-foreground" data-testid="proposed-row-sentences-left-out">
                    <summary className="cursor-pointer">
                      Show the {sentencesLeftOut.length === 1 ? "sentence" : `${sentencesLeftOut.length} sentences`} left out
                    </summary>
                    <ul className="mt-1 space-y-2 border-l border-border pl-3">
                      {sentencesLeftOut.map((sentence, index) => (
                        <li key={`${index}-${sentence.text.slice(0, 24)}`}>
                          <p className="text-foreground">“{sentence.text}”</p>
                          {sentence.reason ? <p className="text-xs">Left out: {sentence.reason}</p> : null}
                        </li>
                      ))}
                    </ul>
                  </details>
                ) : null}
                {researchSentence ? (
                  <p
                    className={
                      extractedResearch?.status === "complete" && (extractedResearch?.sourced ?? 0) > 0
                        ? "text-sm text-muted-foreground"
                        : "text-sm text-amber-700 dark:text-amber-400"
                    }
                    data-testid="proposed-row-research"
                  >
                    {researchSentence}
                  </p>
                ) : null}
                <ChecklistProposalEditor rows={proposalRows} onChange={setProposalRows} disabled={busy} />
              </div>
            ) : null}
          </div>
          <DialogFooter>
            {canDiscard ? (
              <div className="flex items-center gap-2 sm:mr-auto">
                <Button variant="ghost" onClick={handleDiscardExtraction} disabled={busy} data-testid="discard-extraction">
                  <Trash2 className="mr-2 h-4 w-4" />
                  {proposalRows ? "Discard" : "Discard run"}
                </Button>
                {/* Past the ceiling the run may well be healthy; nothing has
                    said otherwise. */}
                {dialogPollStopped && !proposalRows ? (
                  <span className="text-xs text-muted-foreground" data-testid="discard-running-hint">
                    It may still be running. Discarding stops it.
                  </span>
                ) : null}
              </div>
            ) : null}
            <Button variant="outline" onClick={closeAddChecklist} disabled={busy} data-testid="close-add-checklist">
              {extracting || proposalRows ? "Close" : "Cancel"}
            </Button>
            {proposalRows ? (
              <Button onClick={handleConfirmChecklist} disabled={!canConfirm} data-testid="confirm-checklist">
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Confirm {proposalRows.length} item{proposalRows.length === 1 ? "" : "s"}
              </Button>
            ) : extracting ? null : (
              <Button onClick={handleExtract} disabled={!checklistFile || loadingProposal} data-testid="extract-checklist">
                <Upload className="mr-2 h-4 w-4" />
                Extract checklist
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* What a saved checklist actually contains */}
      <ChecklistDetailDialog
        checklist={viewChecklist}
        onOpenChange={(open) => !open && setViewChecklist(null)}
      />

      {/* Rename checklist */}
      <Dialog open={renameChecklist !== null} onOpenChange={(open) => !open && !busy && closeRenameChecklist()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename checklist</DialogTitle>
            <DialogDescription>The new name shows anywhere this checklist can be picked.</DialogDescription>
          </DialogHeader>
          <Input
            value={renameChecklistName}
            onChange={(event) => setRenameChecklistName(event.target.value)}
            aria-label="Checklist name"
          />
          <DialogError message={dialogError} />
          <DialogFooter>
            <Button variant="outline" onClick={closeRenameChecklist} disabled={busy}>
              Cancel
            </Button>
            <Button onClick={handleRenameChecklist} disabled={busy || !renameChecklistName.trim()} data-testid="save-rename">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archive confirmation */}
      <Dialog open={confirmArchive !== null} onOpenChange={(open) => !open && !busy && closeConfirmArchive()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {confirmArchive?.kind === "plan_type" ? "Archive plan type?" : "Archive checklist?"}
            </DialogTitle>
            <DialogDescription>
              {confirmArchive?.kind === "plan_type"
                ? `"${confirmArchive.planType.name}" and its checklists stop appearing in pickers. Completed reviews keep their results.`
                : confirmArchive
                  ? `"${confirmArchive.checklist.name}" stops appearing in pickers. Completed reviews keep their results.`
                  : null}
            </DialogDescription>
          </DialogHeader>
          <DialogError message={dialogError} />
          <DialogFooter>
            <Button variant="outline" onClick={closeConfirmArchive} disabled={busy}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleConfirmArchive} disabled={busy} data-testid="confirm-archive">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Archive
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
